import * as z from 'zod'

/**
 * Where the price of a model came from.
 *
 * `listing` is the public gateway price list matched by id or by name,
 * `reference` is another catalog model the administrator said this one costs
 * like, `manual` is a price typed by hand. A model can only be activated with
 * one of the three: a model with no price would be charged as free, and the
 * columns that carry the cost of a turn have no way to say "unknown".
 */
export enum ModelPriceSource {
    Listing = 'listing',
    Reference = 'reference',
    Manual = 'manual',
}

/**
 * Per-token prices, the same unit the gateway listing publishes, so copying
 * them is literal. The interface multiplies by a million to show them.
 */
export const ModelPricingSchema = z.object({
    inputUsdPerToken: z.number().min(0),
    outputUsdPerToken: z.number().min(0),
    /** Absent: cached input tokens are billed at the input price. */
    cachedInputUsdPerToken: z.number().min(0).optional(),
    /** Absent: cache writes are billed at the input price. */
    cacheWriteUsdPerToken: z.number().min(0).optional(),
})

export type ModelPricing = z.infer<typeof ModelPricingSchema>

export const ModelPriceSchema = z.object({
    source: z.enum(ModelPriceSource),
    /**
     * For `listing`, the gateway id the model was matched with, so a wrong
     * match is visible and correctable; for `reference`, the id chosen.
     */
    reference: z.string().optional(),
    pricing: ModelPricingSchema,
    /** When the price was copied: its freshness date. */
    resolvedAt: z.string(),
})

export type ModelPrice = z.infer<typeof ModelPriceSchema>

export const ModelProviderModelSchema = z.object({
    /** The id the endpoint wants: `gpt-4.1`, `meta-llama/Llama-3.3-70B`. */
    id: z.string().min(1),
    label: z.string().optional(),
    enabled: z.boolean(),
    price: ModelPriceSchema,
})

export type ModelProviderModel = z.infer<typeof ModelProviderModelSchema>

/**
 * The two embedding families an organization may run on its own endpoint.
 *
 * Closed on purpose, and closed in code rather than in the form: both are
 * natively 3072 dimensions, which is the width of the Qdrant collections and
 * cannot be changed after creation, and each one carries its own calibrated
 * relevance floors. Adding a family is a code change that brings its
 * measurements with it.
 */
export const BYOK_EMBEDDING_FAMILIES = [
    'google/gemini-embedding-2',
    'openai/text-embedding-3-large',
] as const

export type ByokEmbeddingFamily = (typeof BYOK_EMBEDDING_FAMILIES)[number]

export const ByokEmbeddingFamilySchema = z.enum(BYOK_EMBEDDING_FAMILIES)

/** Vector width of both families, and of the two Qdrant collections. */
export const BYOK_EMBEDDING_DIMENSIONS = 3072

/**
 * How close the endpoint's vector has to be to the gateway's, for the same
 * sentence, before we believe the endpoint really serves the declared family.
 *
 * Measured on 2026-09-21 against the customer's proxy: the same model scores
 * 0.999999 and a different model of the same width scores -0.0168, so the
 * check has the whole range as margin and still discriminates.
 */
export const BYOK_EMBEDDING_IDENTITY_FLOOR = 0.99

export enum ByokEmbeddingStatus {
    Reindexing = 'reindexing',
    Ready = 'ready',
}

export const ModelProviderEmbeddingSchema = z.object({
    family: ByokEmbeddingFamilySchema,
    /** The id the endpoint wants: `text-embedding-3-large`, `azure/te3-large`. */
    modelId: z.string().min(1),
    price: ModelPriceSchema,
    /** When the identity check of the model passed. */
    verifiedAt: z.string(),
    status: z.enum(ByokEmbeddingStatus),
})

export type ModelProviderEmbedding = z.infer<
    typeof ModelProviderEmbeddingSchema
>

/**
 * `http` is allowed for the same reason `McpServer` allows it: the endpoint
 * that asked for this feature lives inside a VNet. The form warns that the key
 * would travel in clear outside a private network, and asks for a
 * confirmation, not a block: whoever configures this is an administrator of
 * the organization and the network is theirs.
 */
export const ModelProviderBaseUrlSchema = z.url({ protocol: /^https?$/ })

/**
 * Header names and values are checked for control characters before they are
 * ever stored. A value with a newline is a header injection, and it is also a
 * secret leak: `fetch` refuses it with an error whose message quotes the value
 * verbatim, and that message would reach the logs (measured 2026-09-21).
 */
export const HttpHeaderNameSchema = z
    .string()
    .min(1)
    .regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/)
export const HttpHeaderValueSchema = z.string().refine(
    (value) =>
        ![...value].some((character) => {
            const code = character.codePointAt(0) ?? 0
            return code < 0x20 || code === 0x7f
        }),
    { error: 'A header value cannot contain control characters' },
)

export const ModelProviderHeadersSchema = z.record(
    HttpHeaderNameSchema,
    HttpHeaderValueSchema,
)

export const OrganizationModelProviderSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    name: z.string(),
    baseUrl: z.string(),
    /** The key itself never leaves the backend, only whether there is one. */
    hasApiKey: z.boolean(),
    /** Header names only, with the values stripped for the same reason. */
    headerNames: z.array(z.string()),
    queryParams: z.record(z.string(), z.string()),
    models: z.array(ModelProviderModelSchema),
    embedding: ModelProviderEmbeddingSchema.nullable(),
    enabled: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export type OrganizationModelProvider = z.infer<
    typeof OrganizationModelProviderSchema
>

/**
 * One model as the form sends it. `source` and `resolvedAt` are written by the
 * server: `reference` and `pricing` are what the administrator can say, and
 * either of them wins over the listing match instead of following it, so a
 * wrong automatic match can be corrected without the listing winning it back
 * on the next save.
 */
export const ModelProviderModelDtoSchema = z.object({
    id: z.string().min(1),
    label: z.string().optional(),
    enabled: z.boolean(),
    reference: z.string().optional(),
    pricing: ModelPricingSchema.optional(),
})

export type ModelProviderModelDto = z.infer<typeof ModelProviderModelDtoSchema>

export const OrganizationModelProviderDtoSchema = z.object({
    name: z.string().min(1),
    baseUrl: ModelProviderBaseUrlSchema,
    /**
     * `null` keeps the stored key, the same contract as the integrations. An
     * empty string clears it, for an endpoint that stops needing one.
     */
    apiKey: z.string().nullable().optional(),
    /**
     * A value left empty keeps the stored one for that name, the same idea as
     * `apiKey: null`: the form only ever sees the names, so it cannot send a
     * value back. A name left out of the map is removed.
     */
    headers: ModelProviderHeadersSchema.optional(),
    queryParams: z.record(z.string(), z.string()).optional(),
    models: z.array(ModelProviderModelDtoSchema).default([]),
    enabled: z.boolean().default(true),
})

export type OrganizationModelProviderDto = z.infer<
    typeof OrganizationModelProviderDtoSchema
>

export const ModelProviderDiscoverDtoSchema = z.object({
    baseUrl: ModelProviderBaseUrlSchema,
    /** Absent or null reuses the stored key, so discovery works on an edit. */
    apiKey: z.string().nullable().optional(),
    /** Same as the configuration: an empty value reuses the stored one. */
    headers: ModelProviderHeadersSchema.optional(),
    queryParams: z.record(z.string(), z.string()).optional(),
})

export type ModelProviderDiscoverDto = z.infer<
    typeof ModelProviderDiscoverDtoSchema
>

/**
 * What a discovered model is, as far as we can tell.
 *
 * The endpoint's own `/models` does not say: neither LiteLLM nor OpenAI types
 * the entries. The gateway listing does, so the kind is the type of the
 * listing entry the id matches, and `unknown` when nothing matches. An
 * unknown id is treated as a chat model until the probe says otherwise.
 */
export enum DiscoveredModelKind {
    Chat = 'chat',
    Embedding = 'embedding',
    Unknown = 'unknown',
}

export const DiscoveredModelSchema = z.object({
    id: z.string(),
    /**
     * The gateway id this model would take its price from, or null when the
     * listing knows nothing about it: the form uses it to show in advance who
     * will be asked for a reference or a hand-written price.
     */
    listingMatch: z.string().nullable(),
    kind: z.enum(DiscoveredModelKind),
})

export type DiscoveredModel = z.infer<typeof DiscoveredModelSchema>

export const ModelProviderDiscoverResponseSchema = z.object({
    models: z.array(DiscoveredModelSchema),
    /**
     * True when the endpoint does not expose `/models`: the ids have to be
     * typed by hand, which is a limitation of that endpoint, not an error.
     */
    listingUnavailable: z.boolean(),
})

export type ModelProviderDiscoverResponse = z.infer<
    typeof ModelProviderDiscoverResponseSchema
>

/** Where a model in the picker comes from. */
export enum ModelCatalogSource {
    Gateway = 'gateway',
    Byok = 'byok',
}

/**
 * How far a privacy guarantee reaches.
 *
 * Three values and not two, and the middle one is the common case: the
 * gateway serves many models through several upstream providers and the
 * guarantee holds only on some of them, so it routes accordingly. `unknown`
 * is an organization's own endpoint, where we have no basis to claim
 * anything and therefore claim nothing.
 */
export enum ModelGuarantee {
    All = 'all',
    Some = 'some',
    None = 'none',
    Unknown = 'unknown',
}

/**
 * One entry of the catalog the model picker reads, whichever source served
 * it. The frontend derives nothing from the id any more: not the label, not
 * the i18n key, not the brand for the icon.
 */
export const ModelCatalogEntrySchema = z.object({
    /** The id to save on the agent, `byok/` prefixed for the endpoint models. */
    id: z.string(),
    source: z.enum(ModelCatalogSource),
    /** The commercial name from the listing, or the endpoint model's own. */
    label: z.string(),
    /** The producer, or the endpoint name for a model of one. */
    brand: z.string(),
    /** An iconify name, resolved from the producer by the catalog service. */
    brandIcon: z.string(),
    pricing: ModelPricingSchema.optional(),
    /** True when the price is copied from a listing rather than invoiced. */
    priceEstimated: z.boolean(),
    contextWindow: z.number().optional(),
    maxOutputTokens: z.number().optional(),
    /** `text`, `image`, `pdf`, as the listing spells them. */
    inputModalities: z.array(z.string()).optional(),
    reasoning: z
        .object({ effortLevels: z.array(z.string()).optional() })
        .optional(),
    zdr: z.enum(ModelGuarantee),
    noTraining: z.enum(ModelGuarantee),
    /** The gateway no longer lists it: an agent still on it is flagged. */
    retired: z.boolean(),
    /**
     * Not offered to this organization: switched off by its administrator,
     * or its whole source is off (the gateway flag, the endpoint switch).
     * Listed anyway, so the picker can keep an agent's current model visible
     * and marked, and the Models page reads one source for both tables.
     */
    disabled: z.boolean(),
    /**
     * Its whole source is off: the gateway flag, or the endpoint switch. Not
     * the same fact as a model switched off on its own, and the difference
     * is the one that matters to an agent on it: a switched off model keeps
     * answering, a model whose source is off does not. Implies `disabled`.
     */
    sourceOff: z.boolean(),
})

export type ModelCatalogEntry = z.infer<typeof ModelCatalogEntrySchema>

/**
 * Whether an organization can pick this model: neither switched off (by its
 * administrator or with its source) nor retired by the gateway. The one
 * definition the picker, the chip, the defaults and the disconnected banner
 * share.
 */
export const isModelAvailable = (entry: ModelCatalogEntry): boolean =>
    !entry.disabled && !entry.retired

/**
 * Whether the gateway half of a catalog reflects a listing that was read.
 *
 * With the listing unreachable AND no last good copy, every gateway model
 * reads as retired, and membership then proves nothing: whoever validates a
 * model against the catalog has to let a gateway id through instead of
 * blaming the administrator for our outage. Judged on the gateway half
 * alone, because the endpoint's models are never retired.
 */
export const isGatewayCatalogKnown = (catalog: ModelCatalogEntry[]): boolean =>
    catalog.some(
        (entry) =>
            entry.source === ModelCatalogSource.Gateway && !entry.retired,
    )
